import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Database, AlertTriangle, FileCode2, Boxes, Layers, ListTree, ShieldCheck, Shapes, Workflow, Image as ImageIcon, Palette, Sprout } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Card } from '../../../components/ui/Card';
import { getSsotRegistry, SSOT_REGISTRY_COUNT } from '../../../governance';
import type { SsotEntry, SsotDomain, SsotSnapshot } from '../../../governance';
// ─── Domain presentation config ─────────────────────────────────────────────
const DOMAIN_ORDER: SsotDomain[] = ['taxonomy', 'cognitive', 'governance', 'pipeline', 'content', 'visuals', 'mastery', 'design'];
const DOMAIN_CONFIG: Record<SsotDomain, {
  label: string;
  icon: React.ReactNode;
  badge: string;
  dot: string;
  description: string;
}> = {
  taxonomy: {
    label: 'Taxonomie',
    icon: <ListTree className="w-4 h-4" data-id="element-7280" data-mp="1l0zb8sech5gx" />,
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dot: 'bg-indigo-500',
    description: 'Tiers en de canonieke leerstof-strook.'
  },
  cognitive: {
    label: 'Cognitief',
    icon: <Layers className="w-4 h-4" data-id="element-7281" data-mp="1g9h8bysrgutb" />,
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    dot: 'bg-violet-500',
    description: 'DOK-floors/ceilings, cognitieve profielen en bounds.'
  },
  governance: {
    label: 'Governance',
    icon: <ShieldCheck className="w-4 h-4" data-id="element-7282" data-mp="179lu1l3wsjp2" />,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    description: 'Vraagtype-metadata die validatie en gating stuurt.'
  },
  pipeline: {
    label: 'Pipeline',
    icon: <Workflow className="w-4 h-4" data-id="element-7283" data-mp="2nn2jg0ffon8b" />,
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    description: 'Stages, issue-codes, severity-ladder en scoring-gewichten.'
  },
  visuals: {
    label: 'Visuals',
    icon: <ImageIcon className="w-4 h-4" data-id="element-7284" data-mp="4h9ow836wcyi" />,
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    description: 'Visual-rollen per vraagtype.'
  },
  content: {
    label: 'Content',
    icon: <Shapes className="w-4 h-4" data-id="element-7285" data-mp="3rlfzfed1sx7f" />,
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    dot: 'bg-sky-500',
    description: 'Content-structuur SSOTs.'
  },
  mastery: {
    label: 'Mastery',
    icon: <Sprout className="w-4 h-4" data-id="element-7286" data-mp="n66e3xjqcf9v" />,
    badge: 'bg-teal-50 text-teal-700 border-teal-200',
    dot: 'bg-teal-500',
    description: 'Adaptieve-engine parameters, groeistadia en difficulty-proxycontract.'
  },
  design: {
    label: 'Design System',
    icon: <Palette className="w-4 h-4" data-id="element-7287" data-mp="9g317x6qpxx3" />,
    badge: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    dot: 'bg-fuchsia-500',
    description: 'Module-palet, status-/rol-kleuren, mastery- en radius-schaal.'
  }
};
/**
 * SsotRegistryPage — dedicated, top-level admin surface for every catalogued
 * single-source-of-truth. Each domain gets its own tab; within a tab every
 * SSOT is rendered with its metadata and a LIVE values table (read at render
 * time via `entry.read()`). Drift notes surface as amber cautions. The whole
 * surface is read-only and fully data-driven — new registry entries appear
 * automatically with zero changes here.
 */
export function SsotRegistryPage() {
  const entries = useMemo(() => getSsotRegistry(), []);
  const grouped = useMemo(() => {
    const map = new Map<SsotDomain, SsotEntry[]>();
    entries.forEach(entry => {
      const list = map.get(entry.domain) ?? [];
      list.push(entry);
      map.set(entry.domain, list);
    });
    return map;
  }, [entries]);
  // Domains that actually carry entries, in canonical order, with any unknown
  // domains appended so nothing is ever silently hidden.
  const domains = useMemo(() => {
    const present = Array.from(grouped.keys());
    const ordered = DOMAIN_ORDER.filter(d => present.includes(d));
    const extras = present.filter(d => !DOMAIN_ORDER.includes(d));
    return [...ordered, ...extras];
  }, [grouped]);
  const notesCount = useMemo(() => entries.filter(e => e.notes).length, [entries]);
  const [activeTab, setActiveTab] = useState<SsotDomain | ''>(() => domains[0] ?? '');
  const tabs = useMemo(() => domains.map(domain => {
    const cfg = DOMAIN_CONFIG[domain];
    return {
      id: domain,
      label: cfg?.label ?? domain,
      icon: cfg?.icon ?? <Boxes className="w-4 h-4" data-id="element-7288" data-mp="1fhf42usbhgnz" />
    };
  }), [domains]);
  const activeDomain = (activeTab || domains[0]) as SsotDomain | undefined;
  const activeEntries = activeDomain ? grouped.get(activeDomain) ?? [] : [];
  const activeCfg = activeDomain ? DOMAIN_CONFIG[activeDomain] : undefined;
  return <PageLayout title="SSOT Registry" subtitle="Alle single-sources-of-truth — data-driven en live uit de code gelezen" actions={<ConnectionBadge status="live" data-id="element-7290" data-mp="lofz1jtu0m5h" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'SSOT Registry'
  }]} data-id="element-7289" data-mp="34tqqbvkzxhxv">
      {/* ── Summary header ── */}
      <Card className="p-5 mb-5" data-id="element-7291" data-mp="cob1e1efx24i">
        <div className="flex items-start gap-4" data-id="element-7292" data-mp="cp2vt8wawtt7">
          <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0" data-id="element-7293" data-mp="hmuqubs8sw1m">
            <Database className="w-5 h-5" data-id="element-7294" data-mp="15bx6xf78agu0" />
          </div>
          <div className="min-w-0" data-id="element-7295" data-mp="15ejjxucw1ojm">
            <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-7296" data-mp="19ejzjibq45s9">
              Single-Sources-of-Truth
            </h2>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed" data-id="element-7297" data-mp="vludxf03x2vo">
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-7298" data-mp="10kx7o3amzmoc">
                {SSOT_REGISTRY_COUNT}
              </span>{' '}
              gecatalogiseerde single-sources-of-truth, verdeeld over{' '}
              <span className="font-semibold text-gray-900 tabular-nums" data-id="element-7299" data-mp="1t32tsebl69dt">
                {domains.length}
              </span>{' '}
              {domains.length === 1 ? 'domein' : 'domeinen'}. Elke tabel toont
              de actuele waarden zoals de draaiende app ze nu hanteert.
              {notesCount > 0 && <>
                  {' '}
                  <span className="text-amber-700 font-medium" data-id="element-7300" data-mp="1o0zien6tlo5m">
                    {notesCount} {notesCount === 1 ? 'entry' : 'entries'}
                  </span>{' '}
                  met een bekende drift-notitie.
                </>}
            </p>
          </div>
        </div>
      </Card>

      {/* ── Domain tabs ── */}
      {tabs.length > 0 && <AdminTabs tabs={tabs} activeTab={activeDomain ?? ''} onTabChange={id => setActiveTab(id as SsotDomain)} data-id="element-7301" data-mp="1a787pbhcry8b" />}

      {activeCfg?.description && <p className="text-sm text-gray-500 mb-5" data-id="element-7302" data-mp="10d98ak8nysk2">{activeCfg.description}</p>}

      {/* ── Active domain entries ── */}
      <div role="tabpanel" id={`tabpanel-${activeDomain ?? 'none'}`} className="space-y-4" data-id="element-7303" data-mp="37wwtwyl59kyn">
        {activeEntries.length === 0 ? <p className="text-sm text-gray-400 italic" data-id="element-7304" data-mp="3bwoyl07f36yq">
            Geen SSOT-bronnen in dit domein.
          </p> : activeEntries.map((entry, idx) => <SsotEntryCard key={entry.id} entry={entry} dot={activeCfg?.dot ?? 'bg-gray-400'} badge={activeCfg?.badge ?? 'bg-gray-50 text-gray-700 border-gray-200'} domainLabel={activeCfg?.label ?? activeDomain ?? ''} index={idx} data-id="element-7305" data-mp="3lvd3cxae5pkh" data-mpi={idx} />)}
      </div>
    </PageLayout>;
}
// ─── Entry card ───────────────────────────────────────────────────────────
function SsotEntryCard({
  entry,
  dot,
  badge,
  domainLabel,
  index
}: {
  entry: SsotEntry;
  dot: string;
  badge: string;
  domainLabel: string;
  index: number;
}) {
  // LIVE read at render time — never cached, never copied.
  const snapshot = useMemo(() => entry.read(), [entry]);
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.25,
    delay: Math.min(index * 0.04, 0.2)
  }} data-id="element-7306" data-mp="yw5f0yahzkml">
      <Card className="p-5" data-id="element-7307" data-mp="17xwjy0x7rnfe">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-7308" data-mp="1ms9i1jlm6qp0">
          <div className="min-w-0" data-id="element-7309" data-mp="1hu7wms0pufad">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-7310" data-mp="1hswq4kfvytfk">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} data-id="element-7311" data-mp="1vl4lq8vfoeco" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-7312" data-mp="1vn7c4u0i09hx">
                {entry.name}
              </h4>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge}`} data-id="element-7313" data-mp="25jk5trw1mnco">
                {domainLabel}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-1.5 leading-relaxed" data-id="element-7314" data-mp="2k5prao3rmwab">
              {entry.description}
            </p>
          </div>
        </div>

        {/* Metadata row */}
        <div className="flex flex-col gap-2 mb-3 text-[11px]" data-id="element-7315" data-mp="2a8bpnumi4c22">
          <div className="flex items-center gap-2 text-gray-500" data-id="element-7316" data-mp="2a7tn6g98x1lp">
            <FileCode2 className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" data-id="element-7317" data-mp="2f64w8orpv82v" />
            <code className="font-mono text-gray-700 break-all" data-id="element-7318" data-mp="21f84t19j0wxe">
              {entry.file}
            </code>
            <span className="text-gray-300" data-id="element-7319" data-mp="266q3tjrzonfq">·</span>
            <code className="font-mono text-gray-700 break-all" data-id="element-7320" data-mp="268ks982gyxok">
              {entry.exportName}
            </code>
          </div>
          <p className="text-gray-500 leading-relaxed" data-id="element-7321" data-mp="1wg59w9n9g735">
            <span className="font-medium text-gray-700" data-id="element-7322" data-mp="1wgyb6v3ga4dw">Bepaalt:</span>{' '}
            {entry.governs}
          </p>
          {entry.consumers.length > 0 && <div className="flex items-start gap-2 flex-wrap" data-id="element-7323" data-mp="1mhfpoa7w6ajq">
              <span className="font-medium text-gray-700 pt-0.5" data-id="element-7324" data-mp="1md6vzc3j3sl8">
                Consumers:
              </span>
              <div className="flex flex-wrap gap-1" data-id="element-7325" data-mp="1rct81i7mf2sg">
                {entry.consumers.map(consumer => <span key={consumer} className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono text-[10px]" data-id="element-7326" data-mp="1ddacrpzwrlxk">
                    {consumer}
                  </span>)}
              </div>
            </div>}
        </div>

        {/* Drift / caution note */}
        {entry.notes && <div role="note" className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-3" data-id="element-7327" data-mp="1wc63grck5i6n">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-7328" data-mp="2o2kg6k8u4ogq" />
            <p className="text-[11px] text-amber-900 leading-relaxed" data-id="element-7329" data-mp="2o0ps4x29og8f">
              {entry.notes}
            </p>
          </div>}

        {/* Live snapshot table */}
        <SnapshotTable snapshot={snapshot} data-id="element-7330" data-mp="hcpghb9ci1r8" />
      </Card>
    </motion.div>;
}
// ─── Snapshot table ─────────────────────────────────────────────────────────
function SnapshotTable({
  snapshot
}: {
  snapshot: SsotSnapshot;
}) {
  if (snapshot.rows.length === 0) {
    return <p className="text-[11px] text-gray-400 italic px-1 py-2" data-id="element-7331" data-mp="m61teo4nwk45">
        Geen waarden om te tonen.
      </p>;
  }
  return <div className="overflow-x-auto -mx-1 px-1 rounded-lg border border-gray-100" data-id="element-7332" data-mp="3ul28mhiszyag">
      <table className="min-w-full text-[11px]" data-id="element-7333" data-mp="3pn0odt9n5sxe">
        <thead data-id="element-7334" data-mp="8dc4obxr113c">
          <tr className="bg-gray-50" data-id="element-7335" data-mp="8e55yxdxuye3">
            {snapshot.columns.map(col => <th key={col.key} className="text-left font-semibold text-gray-600 uppercase tracking-wider px-3 py-2 whitespace-nowrap border-b border-gray-100" data-id="element-7336" data-mp="3esf81jy5768">
                {col.label}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-7337" data-mp="1lx4f50fwbqi9">
          {snapshot.rows.map((row, rowIdx) => <tr key={rowIdx} className={rowIdx % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'} data-id="element-7338" data-mp="1lyyrhqd3qaaq" data-mpi={rowIdx}>
              {snapshot.columns.map(col => {
            const value = row[col.key];
            return <td key={col.key} className="px-3 py-1.5 text-gray-700 tabular-nums whitespace-nowrap border-b border-gray-50 font-mono" data-id="element-7339" data-mp="20uer0awgayei">
                    {value === undefined || value === '' ? <span className="text-gray-300" data-id="element-7340" data-mp="1834v26tildei">—</span> : value}
                  </td>;
          })}
            </tr>)}
        </tbody>
      </table>
    </div>;
}